import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailApi } from '../../utils/api';
import {
    Mail,
    Plus,
    Loader2,
    Sparkles,
    Send,
    Users,
    BarChart3,
    Eye,
    MousePointer,
    Clock,
    CheckCircle,
    AlertCircle,
    FileText,
    Inbox
} from 'lucide-react';

export default function EmailModule() {
    const { projectId } = useParams();
    const queryClient = useQueryClient();
    const [showGenerator, setShowGenerator] = useState(false);
    const [emailForm, setEmailForm] = useState({
        subject: '',
        topic: '',
        type: 'newsletter',
        tone: 'professional'
    });

    // Fetch campaigns
    const { data: campaignsData, isLoading } = useQuery({
        queryKey: ['email-campaigns', projectId],
        queryFn: () => emailApi.listCampaigns(projectId!),
        enabled: !!projectId
    });

    // Generate campaign mutation
    const generateMutation = useMutation({
        mutationFn: (data: any) => emailApi.generateCampaign(projectId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['email-campaigns', projectId] });
            setShowGenerator(false);
            setEmailForm({ subject: '', topic: '', type: 'newsletter', tone: 'professional' });
        }
    });

    // Send campaign mutation
    const sendMutation = useMutation({
        mutationFn: (id: string) => emailApi.sendCampaign(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-campaigns', projectId] })
    });

    const campaigns = campaignsData?.data?.campaigns || [];

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'sent':
                return { color: 'bg-green-500/20 text-green-400', icon: CheckCircle };
            case 'scheduled':
                return { color: 'bg-blue-500/20 text-blue-400', icon: Clock };
            case 'draft':
                return { color: 'bg-dark-700 text-dark-400', icon: FileText };
            case 'failed':
                return { color: 'bg-red-500/20 text-red-400', icon: AlertCircle };
            default:
                return { color: 'bg-dark-700 text-dark-400', icon: FileText };
        }
    };

    // Calculate totals (using correct column names from schema)
    const totalSent = campaigns.reduce((sum: number, c: any) => sum + (c.sent || 0), 0);
    const totalOpens = campaigns.reduce((sum: number, c: any) => sum + (c.opened || 0), 0);
    const totalClicks = campaigns.reduce((sum: number, c: any) => sum + (c.clicked || 0), 0);
    const sentCampaigns = campaigns.filter((c: any) => c.status === 'sent').length;
    const avgOpenRate = totalSent > 0 ? ((totalOpens / totalSent) * 100).toFixed(1) : '0.0';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Mail className="w-8 h-8 text-green-400" />
                        Email Marketing
                    </h1>
                    <p className="text-dark-400 mt-1">Create and send AI-powered email campaigns</p>
                </div>
                <button
                    onClick={() => setShowGenerator(true)}
                    className="btn btn-primary flex items-center gap-2"
                >
                    <Sparkles className="w-4 h-4" />
                    Generate Email
                </button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Emails Sent', value: totalSent.toLocaleString(), icon: Send, color: 'text-green-400' },
                    { label: 'Total Opens', value: totalOpens.toLocaleString(), icon: Eye, color: 'text-blue-400' },
                    { label: 'Total Clicks', value: totalClicks.toLocaleString(), icon: MousePointer, color: 'text-purple-400' },
                    { label: 'Avg Open Rate', value: `${avgOpenRate}%`, icon: BarChart3, color: 'text-orange-400' }
                ].map((stat) => (
                    <div key={stat.label} className="card p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-dark-400 text-sm">{stat.label}</p>
                                <p className="text-2xl font-bold mt-1">{stat.value}</p>
                            </div>
                            <stat.icon className={`w-8 h-8 ${stat.color} opacity-50`} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Generator Modal */}
            {showGenerator && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-dark-900 rounded-2xl p-6 w-full max-w-lg border border-dark-800">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary-400" />
                            AI Email Generator
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="label">Email Type</label>
                                <select
                                    value={emailForm.type}
                                    onChange={(e) => setEmailForm({ ...emailForm, type: e.target.value })}
                                    className="input"
                                >
                                    <option value="newsletter">Newsletter</option>
                                    <option value="promotional">Promotional</option>
                                    <option value="welcome">Welcome Email</option>
                                    <option value="announcement">Product Announcement</option>
                                    <option value="nurture">Nurture Sequence</option>
                                </select>
                            </div>

                            <div>
                                <label className="label">Subject Line (Optional)</label>
                                <input
                                    type="text"
                                    value={emailForm.subject}
                                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                                    className="input"
                                    placeholder="Leave empty to generate with AI"
                                />
                            </div>

                            <div>
                                <label className="label">Topic / Content Focus</label>
                                <textarea
                                    value={emailForm.topic}
                                    onChange={(e) => setEmailForm({ ...emailForm, topic: e.target.value })}
                                    className="input h-24 resize-none"
                                    placeholder="What should the email be about?"
                                />
                            </div>

                            <div>
                                <label className="label">Tone</label>
                                <select
                                    value={emailForm.tone}
                                    onChange={(e) => setEmailForm({ ...emailForm, tone: e.target.value })}
                                    className="input"
                                >
                                    <option value="professional">Professional</option>
                                    <option value="friendly">Friendly</option>
                                    <option value="urgent">Urgent</option>
                                    <option value="casual">Casual</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setShowGenerator(false)} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button
                                onClick={() => generateMutation.mutate(emailForm)}
                                disabled={!emailForm.topic || generateMutation.isPending}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                {generateMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Sparkles className="w-4 h-4" />
                                )}
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Campaigns List */}
            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                </div>
            ) : campaigns.length === 0 ? (
                <div className="card p-12 text-center">
                    <Inbox className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No email campaigns yet</h3>
                    <p className="text-dark-400 mb-4">Create your first email campaign with AI</p>
                    <button
                        onClick={() => setShowGenerator(true)}
                        className="btn btn-primary inline-flex items-center gap-2"
                    >
                        <Sparkles className="w-4 h-4" />
                        Generate First Email
                    </button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {campaigns.map((campaign: any) => {
                        const statusInfo = getStatusBadge(campaign.status);
                        const StatusIcon = statusInfo.icon;
                        return (
                            <div key={campaign.id} className="card p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${statusInfo.color}`}>
                                                <StatusIcon className="w-3 h-3" />
                                                {campaign.status}
                                            </span>
                                            <span className="text-dark-500 text-sm">
                                                {new Date(campaign.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <h3 className="font-semibold text-lg mb-1">{campaign.subject}</h3>
                                        <p className="text-dark-400 text-sm line-clamp-2">{campaign.previewText || campaign.content?.substring(0, 100)}...</p>
                                    </div>

                                    <div className="flex items-center gap-6 ml-8">
                                        {campaign.status === 'sent' && (
                                            <>
                                                <div className="text-center">
                                                    <p className="text-dark-400 text-xs">Sent</p>
                                                    <p className="font-semibold flex items-center gap-1">
                                                        <Users className="w-4 h-4 text-dark-500" />
                                                        {campaign.sent || 0}
                                                    </p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-dark-400 text-xs">Opens</p>
                                                    <p className="font-semibold flex items-center gap-1">
                                                        <Eye className="w-4 h-4 text-dark-500" />
                                                        {campaign.opened || 0}
                                                    </p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-dark-400 text-xs">Clicks</p>
                                                    <p className="font-semibold flex items-center gap-1">
                                                        <MousePointer className="w-4 h-4 text-dark-500" />
                                                        {campaign.clicked || 0}
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                        {campaign.status === 'draft' && (
                                            <button
                                                onClick={() => sendMutation.mutate(campaign.id)}
                                                disabled={sendMutation.isPending}
                                                className="btn btn-primary btn-sm flex items-center gap-1"
                                            >
                                                {sendMutation.isPending ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Send className="w-3 h-3" />
                                                )}
                                                Send
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
